import { useState, useEffect } from 'react';
import { Clock, RotateCcw, Trash2 } from 'lucide-react';
import { getVersions, deleteVersion, clearHistory } from '../../lib/designer/versionHistory';

function HistoryPanel({ designId, onLoadVersion }) {
  const [versions, setVersions] = useState([]);

  useEffect(() => {
    if (designId) {
      setVersions(getVersions(designId));
    } else {
      setVersions([]);
    }
  }, [designId]);

  function handleDeleteVersion(versionId) {
    const updated = deleteVersion(designId, versionId);
    setVersions(updated);
  }

  function handleClearHistory() {
    if (confirm('Clear all version history for this design?')) {
      clearHistory(designId);
      setVersions([]);
    }
  }

  if (!designId) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center">
        <div className="rounded-full bg-gray-100 dark:bg-gray-800 p-3">
          <Clock className="h-5 w-5 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No design selected</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Save a design to start tracking version history
        </p>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center">
        <div className="rounded-full bg-gray-100 dark:bg-gray-800 p-3">
          <Clock className="h-5 w-5 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No versions yet</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Each save creates a version snapshot you can rollback to
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col p-3 gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Version History ({versions.length})
        </p>
        {versions.length > 0 && (
          <button
            onClick={handleClearHistory}
            className="text-xs text-gray-400 hover:text-red-500"
          >
            Clear All
          </button>
        )}
      </div>

      <ul className="space-y-2">
        {versions.map((v, i) => (
          <li
            key={v.id}
            className={`border rounded-md p-2 transition-colors ${
              i === 0
                ? 'border-teal-300 dark:border-teal-700 bg-teal-50/30 dark:bg-teal-900/10'
                : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  {i === 0 && (
                    <span className="text-xs bg-teal-500 text-white px-1.5 rounded shrink-0">Latest</span>
                  )}
                  <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                    {v.name}
                  </p>
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  {new Date(v.savedAt).toLocaleString()}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {v.nodeCount} nodes &middot; {v.edgeCount} edges
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => onLoadVersion(v)}
                  className="p-1 text-gray-400 hover:text-teal-500 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Restore this version"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => handleDeleteVersion(v.id)}
                  className="p-1 text-gray-400 hover:text-red-500 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Delete this version"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default HistoryPanel;
